import React, { useState } from 'react';
import PublicHeader from '../../components/Layout/PublicHeader';
import PublicFooter from '../../components/Layout/PublicFooter';

interface FAQItem {
    id: number;
    question: string;
    answer: string;
}

const FAQ: React.FC = () => {
    const [openItems, setOpenItems] = useState<number[]>([0]);

    const faqs: FAQItem[] = [
        {
            id: 1,
            question: "What services does We4Task offer?",
            answer: "We4Task is a leading multi-marketing management firm offering a wide range of services including merchant onboarding, merchant retention, educational marketing, fintech marketing, digital onboarding, survey and audit services, and comprehensive marketing solutions tailored to your business needs."
        },
        {
            id: 2,
            question: "How do I get started with We4Task?",
            answer: "Getting started is easy! Simply contact us through our contact page, request a free consultation, or call us directly. Our team will assess your needs and provide a customized solution for your business."
        },
        {
            id: 3,
            question: "What industries does We4Task serve?",
            answer: "We4Task serves clients across various industries including e-commerce, fintech, education, retail, healthcare, and more. Our diverse expertise allows us to provide tailored marketing solutions for businesses of all sizes and sectors."
        },
        {
            id: 4,
            question: "How long does it take to see results?",
            answer: "Results vary depending on the service and campaign type. Typically, you can expect to see initial results within 2-4 weeks, with more significant improvements over 3-6 months. Our team will provide a detailed timeline during your consultation."
        },
        {
            id: 5,
            question: "What is the pricing structure?",
            answer: "Our pricing is customized based on your specific needs, project scope, and requirements. We offer flexible pricing models including project-based, retainer, and performance-based options. Contact us for a detailed quote tailored to your business."
        },
        {
            id: 6,
            question: "Do you offer refunds?",
            answer: "Yes, we have a comprehensive refund policy. Refunds are processed based on the terms agreed upon in your service agreement. Please refer to our Refund Policy page for detailed information, or contact our support team for assistance."
        },
        {
            id: 7,
            question: "Can I cancel my service at any time?",
            answer: "Service cancellation terms depend on your specific agreement. Most services can be cancelled with appropriate notice as outlined in your contract. Please review your service agreement or contact our team to discuss cancellation options."
        },
        {
            id: 8,
            question: "How do you ensure data security and privacy?",
            answer: "We take data security and privacy seriously. We implement industry-standard security measures, comply with data protection regulations, and maintain strict confidentiality protocols. Please review our Privacy Policy for detailed information about how we protect your data."
        },
        {
            id: 9,
            question: "What makes We4Task different from other marketing agencies?",
            answer: "We4Task stands out through our comprehensive approach, experienced team, proven track record, and commitment to delivering measurable results. We combine traditional and digital marketing strategies, offer personalized solutions, and maintain transparent communication throughout the process."
        },
        {
            id: 10,
            question: "Do you provide support after project completion?",
            answer: "Yes, we offer ongoing support and maintenance services. Our team is available to assist with updates, optimizations, and continued growth strategies. Support packages can be customized based on your needs."
        },
        {
            id: 11,
            question: "How can I track the progress of my project?",
            answer: "We provide regular updates through your preferred communication channel, detailed reports, and access to analytics dashboards where applicable. Our project managers maintain transparent communication and are available for regular check-ins."
        },
        {
            id: 12,
            question: "What payment methods do you accept?",
            answer: "We accept various payment methods including bank transfers, credit/debit cards, and other payment gateways. Payment terms and methods will be discussed and agreed upon during the contract phase."
        }
    ];

    const toggleItem = (id: number) => {
        setOpenItems(prev =>
            prev.includes(id)
                ? prev.filter(item => item !== id)
                : [...prev, id]
        );
    };

    return (
        <div className="min-h-screen bg-white">
            <PublicHeader />

            <section className="pt-32 pb-24 bg-gradient-to-br from-blue-50/40 via-white to-orange-50/30 relative overflow-hidden">
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                    <div className="absolute -top-40 -right-40 w-96 h-96 bg-[#3B82F6]/10 rounded-full blur-3xl"></div>
                    <div className="absolute top-60 -left-40 w-96 h-96 bg-[#F59E0B]/10 rounded-full blur-3xl"></div>
                </div>
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <div className="max-w-5xl mx-auto text-center">
                        <div className="inline-flex items-center px-4 py-2 bg-blue-50 border border-blue-100 rounded-full mb-8">
                            <span className="w-2 h-2 bg-[#3B82F6] rounded-full mr-2 animate-pulse"></span>
                            <span className="text-sm font-medium text-[#3B82F6]">Help Center</span>
                        </div>
                        <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold mb-8 leading-tight">
                            <span className="text-[#3B3B3B] inline">Frequently Asked </span>
                            <span className="text-[#3B82F6] inline bg-gradient-to-r from-[#3B82F6] to-blue-500 bg-clip-text text-transparent">Questions</span>
                        </h1>
                        <p className="text-xl sm:text-2xl text-gray-600 leading-relaxed max-w-3xl mx-auto">
                            Find answers to common questions about our services, processes, and policies
                        </p>
                    </div>
                </div>
            </section>

            <section className="py-20 bg-white">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="max-w-4xl mx-auto">
                        <div className="space-y-4">
                            {faqs.map((faq) => (
                                <div
                                    key={faq.id}
                                    className="bg-white border border-gray-200 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden"
                                >
                                    <button
                                        onClick={() => toggleItem(faq.id)}
                                        className="w-full px-8 py-6 text-left flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-[#3B82F6] focus:ring-offset-2 rounded-2xl"
                                    >
                                        <span className="text-base font-semibold text-[#3B3B3B] pr-8">
                                            {faq.question}
                                        </span>
                                        <div className="flex-shrink-0">
                                            <svg
                                                className={`w-6 h-6 text-[#3B82F6] transition-transform duration-300 ${openItems.includes(faq.id) ? 'rotate-180' : ''
                                                    }`}
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M19 9l-7 7-7-7"
                                                />
                                            </svg>
                                        </div>
                                    </button>
                                    <div
                                        className={`overflow-hidden transition-all duration-300 ${openItems.includes(faq.id)
                                            ? 'max-h-96 opacity-100'
                                            : 'max-h-0 opacity-0'
                                            }`}
                                    >
                                        <div className="px-8 pb-6">
                                            <div className="pt-2 border-t border-gray-100">
                                                <p className="text-base text-gray-700 leading-relaxed mt-4">
                                                    {faq.answer}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="mt-16 bg-gradient-to-r from-[#3B82F6] to-[#F59E0B] rounded-2xl p-8 text-center">
                            <h3 className="text-xl font-bold text-white mb-4">
                                Still have questions?
                            </h3>
                            <p className="text-base text-white/90 mb-6">
                                Can't find the answer you're looking for? Our support team is here to help.
                            </p>
                            <a
                                href="/contact"
                                className="inline-flex items-center px-8 py-4 bg-white text-[#3B82F6] rounded-xl hover:bg-gray-50 transition-all duration-300 font-semibold text-base"
                            >
                                Contact Us
                                <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                                </svg>
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            <PublicFooter />
        </div>
    );
};

export default FAQ;

